/* eslint-disable react/jsx-no-bind */
import React from 'react'
import styled from 'styled-components'
import { useFlexSearch } from 'react-use-flexsearch'
import { useStaticQuery, graphql } from 'gatsby'
import { motion } from 'framer-motion'
import parse from 'html-react-parser'

// Elements
import ParseContent from 'components/shared/ParseContent'
import AnchorPrimary from 'components/elements/Anchors/AnchorPrimary'
import Search from 'img/search.inline.svg'

const Wrapper = styled.section``

const Content = styled(ParseContent)`
  & h1,
  & h2,
  & h3,
  & h4,
  & h5,
  & h6 {
    font-size: 20px;
    & strong {
      font-weight: ${({ theme }) => theme.font.weight.bold};
    }
  }
`

const Input = styled.input`
  z-index: 2;
  position: relative;
  padding: 20px 60px 20px 25px;
  height: 35px;
  width: 100%;
  display: flex;
  align-items: center;
  border: 1px solid ${({ theme }) => theme.color.light};
  border-radius: 4px;

  &::placeholder {
    color: rgba(0, 0, 0, 0.4);
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.primary};
    outline-offset: 2px;
  }
`

const SearchForm = styled.form`
  width: 100%;
`

const SearchField = styled.div`
  position: relative;
  width: 100%;
`

const SubmitButton = styled.button`
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 42px;
  height: 42px;
  border: 0;
  border-radius: 4px;
  background: ${({ theme }) => theme.color.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: opacity 0.2s ease-in-out;
  z-index: 2;

  &:hover {
    opacity: 0.9;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.primary};
    outline-offset: 2px;
  }

  & > svg {
    width: 16px;
    height: 16px;
  }
`

const SearchResult = styled(motion.div)`
  box-shadow: 0 0 5px 0 rgba(0, 0, 0, 0.3);
  margin: 20px 0 20px 0;
  background-color: ${({ theme }) => theme.color.light};
`

const Type = styled.p`
  position: relative;
  font-weight: bold;

  &::before {
    content: '';
    position: absolute;
    height: 3px;
    width: 100%;
    left: 0;
    bottom: 0;
  }
`

interface SearchEngineProps {
  // eslint-disable-next-line
  fields: GatsbyTypes.WpPage_Flexcontent_Flex_SearchEngine
}

const SearchEngine: React.FC<SearchEngineProps> = ({ fields }) => {
  const { posts, pages, projects } =
    useStaticQuery<GatsbyTypes.searchQueryQuery>(graphql`
      query searchQuery {
        posts: localSearchPosts {
          index
          store
        }
        pages: localSearchPages {
          index
          store
        }
        projects: localSearchProjects {
          index
          store
        }
      }
    `)

  const [inputValue, setInputValue] = React.useState<string>('')
  const [submittedQuery, setSubmittedQuery] = React.useState<string | null>('')

  const pageResults = useFlexSearch(submittedQuery, pages?.index, pages?.store)

  const postResults = useFlexSearch(submittedQuery, posts?.index, posts?.store)

  const projectsResults = useFlexSearch(
    submittedQuery,
    projects?.index,
    projects?.store
  )

  function handleSearchChange(e: any) {
    setInputValue(e.target.value)
  }

  function handleSearchSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()

    const trimmedValue = inputValue.trim()

    if (!trimmedValue) {
      return
    }

    setInputValue(trimmedValue)
    setSubmittedQuery(trimmedValue)

    if (typeof window !== 'undefined') {
      window.dataLayer = window.dataLayer || []
      window.dataLayer.push({
        event: 'site_search',
        search_term: trimmedValue,
      })
    }
  }

  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const params = urlParams.get('query')

    setInputValue(params || '')
    setSubmittedQuery(params || '')
  }, [])

  return (
    <Wrapper className="container">
      <div className="row mb-5 pb-lg-5 justify-content-center">
        <div className="col-lg-4">
          <Content content={fields.description} />
          <SearchForm onSubmit={handleSearchSubmit}>
            <SearchField>
              <Input
                type="search"
                className=""
                value={inputValue}
                onChange={handleSearchChange}
                placeholder="Zoeken"
              />
              <SubmitButton type="submit" aria-label="Zoekopdracht uitvoeren">
                <Search />
              </SubmitButton>
            </SearchField>
          </SearchForm>
        </div>
      </div>
      <div className="row justify-content-center">
        {submittedQuery !== null &&
          projectsResults.map((r: any, index: number) => (
            <div
              className="col-lg-4 col-sm-6 d-flex flex-grow-1 mb-5"
              // eslint-disable-next-line react/no-array-index-key
              key={index}
            >
              <SearchResult
                initial={{ y: 50, opacity: 0.5 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{
                  duration: 0.8,
                  type: 'spring',
                }}
                className="p-4 w-100 h-100 d-flex flex-column"
              >
                <div className="d-flex justify-content-start">
                  <Type>{r.type}</Type>
                </div>
                <h3>{parse(r.title)}</h3>
                <div className="d-flex justify-content-center mt-auto">
                  <AnchorPrimary href={r.uri}>Ga naar resultaat</AnchorPrimary>
                </div>
              </SearchResult>
            </div>
          ))}
        {submittedQuery !== null &&
          pageResults.map((r: any, index: number) => (
            <div
              className="col-lg-4 col-sm-6 d-flex flex-grow-1 mb-5"
              // eslint-disable-next-line react/no-array-index-key
              key={index}
            >
              <SearchResult
                initial={{ y: 50, opacity: 0.5 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{
                  duration: 0.8,
                  type: 'spring',
                }}
                className="p-4 w-100 h-100 d-flex flex-column"
              >
                <div className="d-flex justify-content-start">
                  <Type>{r.type}</Type>
                </div>
                <h3>{parse(r.title)}</h3>
                <div className="d-flex justify-content-center mt-auto">
                  <AnchorPrimary href={r.uri}>Ga naar resultaat</AnchorPrimary>
                </div>
              </SearchResult>
            </div>
          ))}
        {submittedQuery !== null &&
          postResults.map((r: any, index: number) => (
            <div
              className="col-lg-4 col-sm-6 d-flex flex-grow-1 mb-5"
              // eslint-disable-next-line react/no-array-index-key
              key={index}
            >
              <SearchResult
                initial={{ y: 50, opacity: 0.5 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{
                  duration: 0.8,
                  type: 'spring',
                }}
                className="p-4 w-100 h-100 d-flex flex-column"
              >
                <div>
                  <div className="d-flex justify-content-start">
                    <Type>{r.type}</Type>
                  </div>
                  <h3>{parse(r.title)}</h3>
                </div>

                <div className="d-flex justify-content-center mt-auto">
                  <AnchorPrimary href={r.uri}>Ga naar resultaat</AnchorPrimary>
                </div>
              </SearchResult>
            </div>
          ))}
      </div>
    </Wrapper>
  )
}

export default SearchEngine
